import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  DIALOG_BOX,
  Dialog as DialogRoot,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import {
  IconClose,
} from "@shared/ui/icons";
import { useRestartBlocker } from "@shared/lib/desktop";

export function Field({
  label,
  hint,
  error,
  children,
  style,
}: {
  label: string;
  hint?: ReactNode;
  /**
   * Что не так с этим полем. Занимает место подсказки, а не встаёт под ней:
   * подсказка объясняет, чего от поля ждут, и повторять её рядом с указанием
   * на ошибку значит писать дважды об одном.
   */
  error?: string;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <label className="field" style={style}>
      <span className="field-label">{label}</span>
      {children}
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="field-hint">{hint}</span>
      )}
    </label>
  );
}

/**
 * Подсказка у органа управления — обёртка на один вызов.
 *
 * Нужна потому, что иначе каждое место разрастается впятеро: корень, опора с
 * `asChild`, содержимое. Восемь десятков таких мест — это восемь десятков
 * возможностей забыть `asChild` и получить лишнюю кнопку внутри кнопки.
 *
 * Опора идёт через `asChild`, то есть подсказка не заводит своего узла: она
 * вешается на то, что внутри. Значит внутрь можно класть только то, что
 * принимает `ref` и события, — обычный элемент или наш компонент, но не текст.
 */
export function Hint({
  label,
  side = "top",
  offset,
  children,
}: {
  label: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** Отступ от опоры — когда рядом с ней стоит то, что подсказка закрыла бы. */
  offset?: number;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        asChild
        /*
         * Подсказка по фокусу — только когда фокус видимый.
         *
         * Radix открывает её на любой фокус, кроме пришедшего с нажатия мыши,
         * а фокус ставят не только руками: окно при открытии отдаёт его первой
         * кнопке внутри, всплывающее при закрытии возвращает своей опоре. И
         * там и там подсказка выскакивала сама, без курсора рядом.
         *
         * `:focus-visible` — это и есть вопрос «фокус пришёл с клавиатуры?».
         * Пришедшему с клавиатуры подсказка нужна: навести курсор он не может,
         * и без неё значок для него остаётся без подписи.
         *
         * Отказ идёт через `preventDefault`: Radix составляет свой обработчик
         * с нашим и свой пропускает, если наш отказался.
         */
        onFocus={(event) => {
          if (!event.currentTarget.matches(":focus-visible")) event.preventDefault();
        }}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={offset}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function Notice({
  kind,
  title,
  children,
}: {
  kind: "warn" | "bad";
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className={`notice notice-${kind}`} role="status">
      <div className="notice-title">{title}</div>
      {children && <div className="notice-body">{children}</div>}
    </div>
  );
}

/**
 * Пустое состояние: одна строка о том, что делать дальше, и одно действие.
 * Без рисунков — заполнить их честно всё равно нечем.
 */
export function Empty({
  title,
  body,
  action,
  onAction,
  note,
}: {
  title: string;
  body: string;
  action?: string;
  onAction?: () => void;
  note?: string;
}) {
  return (
    <div className="empty">
      <div className="empty-mark" />
      <div className="empty-title">{title}</div>
      <div className="empty-body">{body}</div>
      {action && (
        <Button type="button" size="sm" style={{ alignSelf: "flex-start" }} onClick={onAction}>
          {action}
        </Button>
      )}
      {note && <div className="empty-note">{note}</div>}
    </div>
  );
}

/**
 * Слои поверх приложения: окно настроек, диалог, вопрос внутри диалога.
 *
 * Esc закрывает только верхний. Без этого одно нажатие снимало бы и вопрос об
 * удалении, и окно настроек под ним — человек ответил бы «нет» и всё равно
 * потерял бы место, на котором стоял.
 *
 * Очередь слоёв держит Radix: свой стек здесь был, но два стека не уживаются,
 * и держать его рядом с чужим означало бы отдавать Esc тому, кто первым
 * подписался, а не тому, кто сверху. Заодно оттуда же приходят ловушка
 * фокуса, возврат фокуса на место после закрытия и `aria` — своего этого
 * здесь не было.
 *
 * Слои ложатся друг на друга порядком в DOM, а не числами: `z-index` у
 * подложки и у окна одинаковый (40), и портал, открытый позже, оказывается
 * выше. Иначе вопрос, заданный поверх настроек, пришлось бы нумеровать вручную
 * — и его затемнение ушло бы под окно, которое оно затемняет.
 */
export function Dialog({
  title,
  onClose,
  children,
  actions,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <DialogRoot open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={DIALOG_BOX}
        /*
         * Пояснения у диалога нет, и без этой строки Radix пишет о том в
         * консоль на каждом открытии. Заголовка ему достаточно.
         */
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          /*
           * Фокус на первое поле, а не на само окно: диалог здесь почти
           * всегда о вводе, и лишнее нажатие Tab перед набором — это
           * лишнее нажатие в каждом из них.
           */
          const first = event.currentTarget as HTMLElement | null;
          const target = first?.querySelector<HTMLElement>("input, textarea, button");

          if (target) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        {children}
        {actions && <DialogFooter>{actions}</DialogFooter>}
      </DialogContent>
    </DialogRoot>
  );
}

/**
 * Окно настроек — сервера и канала.
 *
 * Это модальное окно, а не отдельная страница: приложение остаётся под
 * затемнением, звонок не рвётся, а закрытие возвращает ровно туда, откуда
 * вошли. Оболочка одна на оба экрана — одна шапка, один Esc, одна кнопка
 * закрытия; меняются только разделы слева.
 *
 * Закрытие идёт одной дорогой: Esc, клик по фону и «✕» ведут в неё же.
 * Иначе вопрос о несохранённых правках пришлось бы вешать в трёх местах —
 * и он рано или поздно отвалился бы в одном.
 */
export function SettingsModal({
  title,
  subtitle,
  size = "narrow",
  dirty = false,
  nav,
  onClose,
  children,
}: {
  title: string;
  /*
   * Не строка: у настроек канала подпись начинается со значка типа канала, а
   * значок — компонент. Собирать его обратно в символ ради типа означало бы
   * держать в интерфейсе два разных способа показать одно и то же.
   */
  subtitle?: ReactNode;
  /**
   * Ширина окна тремя ступенями.
   *
   * `narrow` — раздел в одну колонку: имя, описание, пара полей.
   * `wide` — таблица или редактор прав: в узкое окно они не влезают.
   * `broad` — настройки сервера целиком: там колонка разделов, список ролей
   * и сами права стоят в три столбца, и каждому нужно место.
   */
  size?: "narrow" | "wide" | "broad";
  /** Есть несохранённые правки — тогда закрытие сначала спросит. */
  dirty?: boolean;
  /**
   * Колонка разделов. С ней окно раскладывается по макету v6: колонка
   * слева во всю высоту, а шапка — над содержимым справа, и в ней не имя
   * окна, а имя раздела (`title`) с подсказкой к нему (`subtitle`).
   */
  nav?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const [asking, setAsking] = useState(false);
  const restartId = useId();
  useRestartBlocker(
    dirty
      ? {
          id: `dirty-settings:${restartId}`,
          title: `Не сохранены правки: ${title}`,
          detail: "сохраните их или закройте окно без сохранения",
          kind: "wait",
        }
      : null,
  );

  /*
   * Окно вырастает из того, что его открыло, и уходит обратно туда же — так
   * в макете v6 настройки растут из шестерёнки. Откуда, видно по фокусу:
   * нажатая кнопка держит его в тот миг, когда окно уже встало, а Radix ещё
   * не перевёл фокус внутрь. Открыли не кнопкой — окно просто подрастает на
   * месте.
   */
  const box = useRef<HTMLDivElement>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const closing = useRef(false);
  const appeared = useRef(false);

  /*
   * Запускается, когда узел окна встал в документ, а не эффектом: Radix
   * кладёт окно в портал уже после первой отрисовки, и эффект застал бы
   * пустую ссылку.
   */
  const appear = useCallback((el: HTMLDivElement | null) => {
    box.current = el;
    if (!el || appeared.current) return;
    appeared.current = true;
    const from = document.activeElement;
    if (from instanceof HTMLElement && from !== document.body && !el.contains(from)) {
      const r = from.getBoundingClientRect();
      origin.current = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = el.getBoundingClientRect();
    const o = origin.current;
    el.animate(
      [
        {
          transform: o
            ? `translate(${o.x - (r.left + r.width / 2)}px, ${o.y - (r.top + r.height / 2)}px) scale(0.05)`
            : "scale(0.92)",
        },
        { transform: "none" },
      ],
      { duration: o ? 560 : 340, easing: "linear(0, 0.08 4%, 0.3 9%, 0.62 15%, 0.88 22%, 1.02 29%, 1.05 34%, 1.04 40%, 1.01 50%, 1 62%, 0.998 75%, 1)" },
    );
    // Прозрачность — у содержимого, не у окна: полупрозрачное окно отрезало бы своему размытию фон.
    el.querySelectorAll(":scope > *").forEach((part) =>
      part.animate(
        [
          { opacity: 0, filter: "blur(10px)" },
          { opacity: 0, filter: "blur(10px)", offset: 0.25 },
          { opacity: 1, filter: "blur(0px)" },
        ],
        { duration: 520, easing: "ease-out" },
      ),
    );
  }, []);

  /** Уйти обратно туда, откуда вырос, — и закрыться, когда ушёл. */
  const close = useCallback(() => {
    const el = box.current;
    if (!el || closing.current || matchMedia("(prefers-reduced-motion: reduce)").matches) return onClose();
    closing.current = true;
    const r = el.getBoundingClientRect();
    const o = origin.current;
    const to = o
      ? `translate(${o.x - (r.left + r.width / 2)}px, ${o.y - (r.top + r.height / 2)}px) scale(0.05)`
      : "scale(0.95)";
    const fade = { duration: 220, easing: "cubic-bezier(.5,0,.75,0)", fill: "forwards" as const };
    el.querySelectorAll(":scope > *").forEach((part) =>
      part.animate([{ opacity: 1 }, { opacity: 0 }], { ...fade, duration: 120 }),
    );
    el.animate([{ transform: "none" }, { transform: to }], {
      duration: 240,
      easing: "cubic-bezier(.5,0,.75,0)",
      fill: "forwards",
    }).onfinish = onClose;
  }, [onClose]);

  const leave = useCallback(() => {
    if (dirty) setAsking(true);
    else close();
  }, [dirty, close]);

  return (
    <DialogRoot open onOpenChange={(open) => !open && leave()}>
      <DialogContent
        /*
         * Высота одна на все разделы, а ширина — по ступени. Раздел, который
         * меняет окну рост при переключении, прыгает под курсором; ширина же
         * меняется редко и по делу.
         *
         * Ступени записаны отдельными классами, а не числом в стиле: величина
         * известна на сборке, и утилите Tailwind её надо видеть строкой —
         * собранной на лету она в готовый файл не попадёт.
         *
         * Верхний предел всюду один — окно не прижимается к краям экрана
         * вплотную, даже когда просит больше, чем экран может дать.
         */
        ref={appear}
        data-zoom=""
        className={`h-[min(712px,calc(100%-var(--space-8)))] surface-pop rounded-[34px]
          ${
            size === "broad"
              ? "w-[min(1200px,calc(100%-var(--space-8)))]"
              : size === "wide"
                ? "w-[min(900px,calc(100%-var(--space-8)))]"
                : "w-[min(660px,calc(100%-var(--space-8)))]"
          }`}
        aria-describedby={undefined}
          /*
           * Пока висит вопрос о несохранённых правках, окно под ним на Esc и
           * на щелчок мимо не отзывается: иначе один Esc снял бы и вопрос, и
           * окно разом — ровно то, ради чего вопрос и задан.
           *
           * Стек Radix сам отдаёт Esc верхнему слою, но щелчок мимо приходит
           * обоим: он лежит вне обоих окон.
           */
        onEscapeKeyDown={(event) => asking && event.preventDefault()}
        onPointerDownOutside={(event) => asking && event.preventDefault()}
        onInteractOutside={(event) => asking && event.preventDefault()}
      >
        {nav ? (
          <div className="chan-settings set-split">
            {nav}
            <div className="chan-settings-body">
              <header className="set-head">
                <span className="set-head-name">
                  <DialogTitle className="set-head-title">{title}</DialogTitle>
                  {subtitle && <span className="set-head-hint ellipsis">{subtitle}</span>}
                </span>
                <span className="modal-esc">ESC</span>
                <button type="button" className="icon-btn modal-close" aria-label="Закрыть" onClick={leave}>
                  <IconClose size={17} />
                </button>
              </header>
              {children}
            </div>
          </div>
        ) : (
          <>
            <header className="modal-head">
              {/* Заголовок окна тише заголовка вопроса: это шапка, а не обращение. */}
              <DialogTitle className="flex-none text-[13.5px] text-foreground">{title}</DialogTitle>
              {subtitle && <span className="modal-sub ellipsis">{subtitle}</span>}
              <span className="grow" />
              <span className="modal-esc">ESC</span>
              <Hint label="Закрыть — Esc">
                <button type="button" className="icon-btn modal-close" onClick={leave}>
                  <IconClose size={14} />
                </button>
              </Hint>
            </header>

            <div className="modal-body">
              {children}
            </div>
          </>
        )}
      </DialogContent>

      {asking && (
        <Dialog
          title="Закрыть без сохранения?"
          onClose={() => setAsking(false)}
          actions={
            <>
              <span className="grow" />
              <Button variant="secondary" size="sm" type="button" onClick={() => setAsking(false)}>
                Вернуться
              </Button>
              <Button variant="destructive" size="sm" type="button" onClick={onClose}>
                Закрыть
              </Button>
            </>
          }
        >
          <div className="small" style={{ color: "var(--ui-text-soft)", lineHeight: 1.6 }}>
            Правки в этом окне не сохранены. Закрыть — значит их потерять.
          </div>
        </Dialog>
      )}
    </DialogRoot>
  );
}

/** Тикает раз в секунду — для обратного отсчёта на экране входа. */
export function useTick(active: boolean) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);
}
